import React, { useState, useEffect, useCallback, useRef } from 'react';
import { devLog, devWarn, devError } from '../../utils/devLogger';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useLang } from '../../context/LangContext';
import { useUserAuth } from '../../context/UserAuthContext';
import AuctionCard from '../../customComponents/AuctionCard';
import NoDataView from '../../components/NoDataContainer';
import FilterChips, { ChipItem } from '../../components/common/FilterChips';
import DataLoader from '../../components/DataLoader';
import { getWonAuctions } from '../../lib/api/user/userApi';
import { WonAuctionData } from '../../lib/api/user/userApi.types';

const MainContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  flex: 1;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  flex: 1;
  min-height: 0; /* flex item이 제대로 축소될 수 있도록 */
`;

const Container = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  width: 100%;
  padding: 0 0 80px 0;
  
  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
    gap: 24px;
  }
`;

const ChipsDivider = styled.div`
  width: 100%;
  height: 1px;
  background-color: #e5e5e5;
  margin-top: 16px;
  margin-bottom: 8px;

  @media (min-width: 768px) {
    margin-top: 8px;
  }
`;

// 경매 카드 컨테이너 -> AuctionCard 컴포넌트로 대체되므로 제거



const wonAuctionsTexts = {
  ko: {
    title: '나의 낙찰 내역',
    noData: '낙찰 내역이 없습니다.',
    wonLotCount: '낙찰된 LOT 개수',
    totalPayment: '총 낙찰가',
    wonComplete: '입찰 완료',
    // 필터 칩 텍스트들
    filters: {
      all: '전체',
      pending: '결제 대기',
      completed: '결제 완료'
    }
  },
  en: {
    title: 'My Won Auctions',
    noData: 'No won auctions found.',
    wonLotCount: 'Won LOT Count',
    totalPayment: 'Total Bid Amount',
    wonComplete: 'Bid Completed',
    // 필터 칩 텍스트들
    filters: {
      all: 'All',
      pending: 'Payment Pending',
      completed: 'Payment Completed'
    }
  }
};





const WonAuctions: React.FC = () => {
  const { lang } = useLang();
  const { user } = useUserAuth();
  const navigate = useNavigate();
  const texts = wonAuctionsTexts[lang];
  
  // 상태 관리
  const [wonAuctions, setWonAuctions] = useState<WonAuctionData[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  
  // 필터 상태 관리
  const [activeFilter, setActiveFilter] = useState<string>('all');
  
  // Intersection Observer를 위한 ref
  const observerTarget = useRef<HTMLDivElement>(null);

  // 필터 칩 데이터 생성
  const getFilterChips = (): ChipItem[] => {
    return [
      { key: 'all', label: texts.filters.all },
      { key: 'pending', label: texts.filters.pending },
      { key: 'completed', label: texts.filters.completed }
    ];
  };

  // 필터 칩 클릭 핸들러
  const handleFilterChipClick = (filterKey: string) => {
    setActiveFilter(filterKey);
    setCurrentPage(1);
    setHasMore(true);
    devLog('Filter changed to:', filterKey);
    // 필터 변경 시 첫 페이지부터 다시 로드
    fetchWonAuctions(1, true, filterKey);
  };

  // 낙찰 경매 API 호출 함수
  const fetchWonAuctions = useCallback(async (page: number, isInitial: boolean = false, status: string = 'all') => {
    if (!user || !user.index) {
      setLoading(false);
      return;
    }

    try {
      if (isInitial) {
        setLoading(true);
        setError(null);
      } else {
        setLoadingMore(true);
      }
      
      // status를 API 파라미터로 변환
      let apiStatus: 'ALL' | 'PENDING' | 'PAID' | undefined;
      if (status === 'all') {
        apiStatus = 'ALL';
      } else if (status === 'pending') {
        apiStatus = 'PENDING';
      } else if (status === 'completed') {
        apiStatus = 'PAID';
      }
      
      // 실제 API 호출
      const response = await getWonAuctions({
        page,
        limit: 30,
        status: apiStatus
      });
      
      if (response.data.statusCode === 200 && response.data.data) {
        const auctionData = response.data.data;
        
        if (isInitial) {
          setWonAuctions(auctionData);
        } else {
          // 기존 데이터에 새 데이터 누적
          setWonAuctions(prev => [...prev, ...auctionData]);
        }
        
        // 받은 데이터가 30개 미만이면 더 이상 데이터가 없음
        setHasMore(auctionData.length === 30);
        setCurrentPage(page);
      } else {
        if (isInitial) {
          setWonAuctions([]);
          setError(response.data.message || '낙찰 내역을 불러올 수 없습니다.');
        }
        setHasMore(false);
      }
    } catch (error) {
      devError('Failed to fetch won auctions:', error);
      if (isInitial) {
        setWonAuctions([]);
        setError('낙찰 내역을 불러오는 중 오류가 발생했습니다.');
      }
      setHasMore(false);
    } finally {
      if (isInitial) {
        setLoading(false);
      } else {
        setLoadingMore(false);
      }
    }
  }, [user]);

  // 초기 데이터 로드
  useEffect(() => {
    fetchWonAuctions(1, true, activeFilter);
  }, [fetchWonAuctions, activeFilter]);

  // Intersection Observer를 통한 무한 스크롤 구현
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && hasMore && !loading && !loadingMore) {
          fetchWonAuctions(currentPage + 1, false, activeFilter);
        }
      },
      {
        threshold: 0.1, // 10% 보이면 다음 페이지 로드
        rootMargin: '100px' // 100px 전에 미리 로드
      }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [hasMore, loading, loadingMore, currentPage, fetchWonAuctions, activeFilter]);

  const handleCardClick = (auction: WonAuctionData) => {
    devLog('낙찰 내역 클릭:', auction);
    // 낙찰상세 페이지로 이동 (auction 데이터를 state로 전달)
    navigate(`/won-auction-detail/${auction.index}`, {
      state: {
        auctionData: auction
      }
    });
  };

  const handlePaymentClick = (auction: any) => {
    devLog('결제하기 클릭:', auction);
    devLog('auction 객체의 모든 키:', Object.keys(auction));
    devLog('auction.auctioneer:', auction.auctioneer);
    
    if (!auction) {
      devError('auction 객체가 없습니다.');
      return;
    }

    // 결제 안내 페이지로 이동
    navigate('/payment-guide', {
      state: { 
        auctionData: auction
      }
    });
  };

  const renderContent = () => {
    // 로딩 상태
    if (loading) {
      return (
        <div style={{ 
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flex: 1,
          width: '100%',
          minHeight: 'calc(100vh - 200px)'
        }}>
          <DataLoader/>
        </div>
      );
    }

    // 에러 상태
    if (error) {
      return (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flex: 1,
          width: '100%',
          minHeight: 'calc(100vh - 200px)'
        }}>
          <NoDataView message={texts.noData} imageSize={107} />
        </div>
      );
    }

    // 데이터가 없을 때
    if (wonAuctions.length === 0) {
      return (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flex: 1,
          width: '100%',
          minHeight: 'calc(100vh - 200px)'
        }}>
          <NoDataView message={texts.noData} imageSize={107} />
        </div>
      );
    }

    return (
      <>
        <Container>
          {wonAuctions.map((auction, index) => {
            // 사용자의 locationType에 따라 표시할 금액 결정
            const isDomestic = user?.locationType === 'DOMESTIC';
            
            // lots 배열에서 amount 또는 usd 값들을 합산
            let displayAmount = 0;
            if (auction.lots && Array.isArray(auction.lots)) {
              displayAmount = auction.lots.reduce((sum, lot) => {
                const lotAmount = isDomestic ? (lot.amount || 0) : (lot.usd || 0);
                return sum + lotAmount;
              }, 0);
            } else {
              // lots가 없으면 기존 totalAmount 사용
              displayAmount = auction.totalAmount || 0;
            }
            
            return (
              <AuctionCard 
                key={auction.index || `auction-${index}`}
                auction={auction}
                lang={lang}
                type="won"
                onClick={handleCardClick}
                onPayment={handlePaymentClick}
                user={user} // user 정보 전달
                extraData={{
                  wonLotCount: auction.totalWonLots,
                  totalPayment: displayAmount,
                  completedAt: auction.isPaid ? auction.paymentCompletedAt : undefined,
                  isPaid: auction.isPaid,
                  paymentRequestDate: auction.paymentRequestDate,
                  paymentDueDate: auction.paymentDueAt
                }}
              />
            );
          })}
        </Container>
        
        {/* Intersection Observer 타겟 */}
        <div ref={observerTarget} style={{ height: '20px' }} />
        
        {/* 추가 로딩 상태 */}
        {loadingMore && (
          <div style={{ 
            padding: '20px', 
            textAlign: 'center',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            <div
              style={{
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                border: '3px solid #e0e0e0',
                borderTop: '3px solid #666',
                animation: 'spin 1s linear infinite',
              }}
            />
            <style>{`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        )}
        
        {/* 더 이상 데이터가 없을 때 */}
        {!hasMore && wonAuctions.length > 0 && (
          <div style={{ 
            padding: '20px', 
            textAlign: 'center', 
            color: '#999',
            fontSize: '14px'
          }}>
            모든 낙찰 내역을 불러왔습니다.
          </div>
        )}
      </>
    );
  };

  return (
    <MainContainer>
      {/* 필터 칩스 */}
      <FilterChips
        chips={getFilterChips()}
        selectedChips={[activeFilter]}
        onChipClick={handleFilterChipClick}
        allowMultiSelect={false}
      />
      
      {/* 디바이더 */}
      <ChipsDivider />
      
      <ContentContainer>
        {renderContent()}
      </ContentContainer>
    </MainContainer>
  );
};
export default WonAuctions;
